import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface QualityAuditError {
  id: string;
  apiName?: string;
  message: string;
  originalError?: any;
  context?: any;
}

interface QualityAuditState {
  selectedRole: string;
  selectedRegion: string;
  selectedDates: string[];
  selectedRowData?: any;
  randomObservations?: any[];
  currentObservationIndex?: number;
  modifiedBy: string | null;
  createdBy: string | null;
  region: string | null;
  site: string | null;
  selectedFilters: {
    [key: string]: any;
  };
  // Add audit completion status mapping
  auditCompletionStatus: {
    [auditId: string]: {
      currentCount: number;
      totalRequired: number;
      isCompleted: boolean;
    };
  };
  // Add review audit data storage
  reviewAuditData: {
    [observationId: string]: {
      reviewType: { id: string; displayText: string } | null;
      reviewStatus: { id: string; displayText: string }[];
      reviewComment: string;
    };
  };
  // Error / loading state for quality audit module
  error: string | null;
  isLoading: boolean;
  // Centralized error handling (mirrors observation/dlo patterns)
  centralizedErrors: {
    errors: QualityAuditError[];
    activeError: QualityAuditError | null;
    isErrorModalVisible: boolean;
  };
}

const initialState: QualityAuditState = {
  selectedRole: "",
  selectedRegion: "",
  selectedDates: [],
  selectedRowData: undefined,
  randomObservations: [],
  currentObservationIndex: undefined,
  modifiedBy: null,
  createdBy: null,
  region: null,
  site: null,
  selectedFilters: {},
  // Initialize audit completion status
  auditCompletionStatus: {},
  // Initialize review audit data
  reviewAuditData: {},
  // Initialize error/loading
  error: null,
  isLoading: false,
  // Centralized errors
  centralizedErrors: {
    errors: [],
    activeError: null,
    isErrorModalVisible: false,
  },
};

export const qualityAuditSlice = createSlice({
  name: "qualityAudit",
  initialState,
  reducers: {
    // Generic error/loading reducers for UI and async flows
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      // Follow pattern used across other slices: if an error is set, stop loading
      if (action.payload) {
        state.isLoading = false;
      }
    },
    clearError(state) {
      state.error = null;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setSelectedRole(state, action: PayloadAction<string>) {
      state.selectedRole = action.payload;
    },
    setSelectedRegion(state, action: PayloadAction<string>) {
      state.selectedRegion = action.payload;
    },
    setSelectedDates(state, action: PayloadAction<string[]>) {
      state.selectedDates = action.payload;
    },
    setSelectedRowData(state, action: PayloadAction<any>) {
      state.selectedRowData = action.payload;
    },
    setRandomObservations(state, action: PayloadAction<any[]>) {
      state.randomObservations = action.payload;
    },
    setCurrentObservationIndex(state, action: PayloadAction<number>) {
      state.currentObservationIndex = action.payload;
    },
    resetRandomObservationsAndIndex(state) {
      state.randomObservations = [];
      state.currentObservationIndex = undefined;
    },
    resetSelectedRowData(state) {
      state.selectedRowData = undefined;
    },
    setmodifiedBy(state, action: PayloadAction<string | null>) {
      state.modifiedBy = action.payload;
    },
    setCreatedBy(state, action: PayloadAction<string | null>) {
      state.createdBy = action.payload;
    },
    setRegion(state, action: PayloadAction<string | null>) {
      state.region = action.payload;
    },
    setSite(state, action: PayloadAction<string | null>) {
      state.site = action.payload;
    },
    setSelectedFilters(state, action: PayloadAction<{ [key: string]: any }>) {
      state.selectedFilters = action.payload;
    },
    resetFilters(state) {
      state.selectedFilters = {};
      state.selectedRole = "";
      state.selectedRegion = "";
    },
    // --- Audit completion status actions ---
    setAuditCompletionStatus(
      state,
      action: PayloadAction<{
        [auditId: string]: {
          currentCount: number;
          totalRequired: number;
          isCompleted: boolean;
        };
      }>
    ) {
      state.auditCompletionStatus = action.payload;
    },
    // --- Review audit data actions ---
    setReviewAuditData(
      state,
      action: PayloadAction<{
        observationId: string;
        reviewType: { id: string; displayText: string } | null;
        reviewStatus: { id: string; displayText: string }[];
        reviewComment: string;
      }>
    ) {
      const { observationId, reviewType, reviewStatus, reviewComment } = action.payload;
      state.reviewAuditData[observationId] = {
        reviewType,
        reviewStatus,
        reviewComment,
      };
    },
    clearReviewAuditData(
      state,
      action: PayloadAction<string>
    ) {
      const observationId = action.payload;
      delete state.reviewAuditData[observationId];
    },
    resetAllReviewAuditData(state) {
      state.reviewAuditData = {};
    },

    // Centralized error handling actions (mirrors observation/dlo patterns)
    addCentralizedError(state, action: PayloadAction<QualityAuditError>) {
      state.centralizedErrors.errors.push(action.payload);
      // Keep only last 10 errors to avoid memory issues
      if (state.centralizedErrors.errors.length > 10) {
        state.centralizedErrors.errors = state.centralizedErrors.errors.slice(-10);
      }
    },
    showErrorModal(state, action: PayloadAction<QualityAuditError>) {
      state.centralizedErrors.activeError = action.payload;
      state.centralizedErrors.isErrorModalVisible = true;
      // also add to the error list
      state.centralizedErrors.errors.push(action.payload);
      if (state.centralizedErrors.errors.length > 10) {
        state.centralizedErrors.errors = state.centralizedErrors.errors.slice(-10);
      }
    },
    hideErrorModal(state) {
      state.centralizedErrors.activeError = null;
      state.centralizedErrors.isErrorModalVisible = false;
    },
    setActiveError(state, action: PayloadAction<QualityAuditError | null>) {
      state.centralizedErrors.activeError = action.payload;
    },
    removeErrorById(state, action: PayloadAction<string>) {
      const id = action.payload;
      state.centralizedErrors.errors = state.centralizedErrors.errors.filter(
        (e) => e.id !== id
      );
      if (state.centralizedErrors.activeError?.id === id) {
        state.centralizedErrors.activeError = null;
        state.centralizedErrors.isErrorModalVisible = false;
      }
    },
    clearCentralizedErrors(state) {
      state.centralizedErrors.errors = [];
      state.centralizedErrors.activeError = null;
      state.centralizedErrors.isErrorModalVisible = false;
    },

    // Memory cleanup actions
    limitRandomObservations: (state, action: PayloadAction<number>) => {
      const maxObservations = action.payload || 100;
      if (state.randomObservations && state.randomObservations.length > maxObservations) {
        state.randomObservations = state.randomObservations.slice(-maxObservations);
        // Reset index if it's out of bounds
        if (state.currentObservationIndex !== undefined && 
            state.currentObservationIndex >= state.randomObservations.length) {
          state.currentObservationIndex = state.randomObservations.length - 1;
        }
      }
    },
    clearAuditCompletionStatus: (state) => {
      state.auditCompletionStatus = {};
    },
    clearSelectedRowData: (state) => {
      state.selectedRowData = undefined;
      state.randomObservations = [];
      state.currentObservationIndex = undefined;
    },
    resetQualityAuditState: (state) => {
      // Reset to initial state while preserving user selections
      const preservedSelections = {
        selectedRole: state.selectedRole,
        selectedRegion: state.selectedRegion,
      };
      Object.assign(state, initialState, preservedSelections);
    },
  },
});

export const {
  setError,
  clearError,
  setLoading,
  setSelectedRole,
  setSelectedRegion,
  setSelectedDates,
  setSelectedRowData,
  setRandomObservations,
  setCurrentObservationIndex,
  resetRandomObservationsAndIndex,
  resetSelectedRowData,
  setmodifiedBy,
  setCreatedBy,
  setRegion,
  setSite,
  setSelectedFilters,
  resetFilters,
  setAuditCompletionStatus,
  setReviewAuditData,
  clearReviewAuditData,
  resetAllReviewAuditData,

  // Centralized error handling actions
  addCentralizedError,
  showErrorModal,
  hideErrorModal,
  setActiveError,
  removeErrorById,
  clearCentralizedErrors,

  // Memory cleanup actions
  limitRandomObservations,
  clearAuditCompletionStatus,
  clearSelectedRowData,
  resetQualityAuditState,
} = qualityAuditSlice.actions;

export default qualityAuditSlice.reducer;
